import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Layers, Package, Trash2, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScormMultiImportButton } from "../components/lms/ScormImportButton";

/** /admin/formation/modules — Modules de cours = compilations ordonnées de séquences SCORM. */
export default function CourseModulesPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/lms/lessons"); setItems(r.data || []); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const createEmpty = async () => {
    try {
      const r = await api.post("/lms/lessons", { title: "Nouveau module de cours" });
      navigate(`/admin/formation/modules/${r.data.lesson_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (m) => {
    if (!window.confirm(`Supprimer le module « ${m.title} » et ses ${(m.module_ids || []).length} séquence(s) SCORM ?`)) return;
    try { await api.delete(`/lms/lessons/${m.lesson_id}`); toast.success("Module supprimé"); load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? items.filter((m) => (m.title || "").toLowerCase().includes(q) || (m.description || "").toLowerCase().includes(q)) : items;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="course-modules-page">
      <PageHeader
        icon={<Layers />}
        title="Modules de cours"
        description="Compilez vos séquences SCORM en modules de cours : importez plusieurs packages, ordonnez-les, publiez, puis rattachez le module à vos programmes."
        breadcrumb={[{ label: "Formation" }, { label: "Modules de cours" }]}
        testId="course-modules-header"
        actions={(
          <div className="flex items-center gap-2">
            <ScormMultiImportButton api={api} label="Importer des SCORM → nouveau module" testId="cm-new-import" onDone={(d) => navigate(`/admin/formation/modules/${d.lesson.lesson_id}`)} />
            <Button onClick={createEmpty} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="course-module-new-btn"><Plus className="w-4 h-4 mr-2" /> Nouveau module</Button>
          </div>
        )}
      />

      <div className="flex gap-3 items-center mb-4">
        <Input placeholder="Rechercher un module…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#0b0811] border-white/10 text-white max-w-sm" data-testid="course-modules-search" />
        <div className="text-xs text-slate-500">{filtered.length} module{filtered.length > 1 ? "s" : ""}</div>
      </div>

      {loading ? (
        <ListSkeleton items={4} testId="course-modules-loading" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-[#0b0811]" data-testid="course-modules-empty">
          <Package className="w-10 h-10 mx-auto text-slate-700 mb-3" />
          <p className="text-sm text-slate-500 mb-3">Aucun module de cours — importez vos packages SCORM d'un coup pour créer le premier.</p>
          <Button onClick={createEmpty} variant="ghost" className="text-[#f7c935]"><Plus className="w-4 h-4 mr-2" />Créer un module vide</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="course-modules-grid">
          {filtered.map((m) => <ModuleCard key={m.lesson_id} m={m} onEdit={() => navigate(`/admin/formation/modules/${m.lesson_id}`)} onDelete={() => del(m)} />)}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ m, onEdit, onDelete }) {
  const n = (m.module_ids || []).length;
  return (
    <div className="group bg-[#14101c] border border-white/5 rounded-xl p-5 hover:border-[#f7c935]/30 transition-colors cursor-pointer" onClick={onEdit} data-testid={`course-module-card-${m.lesson_id}`}>
      <div className="font-bold text-white text-base truncate">{m.title || "Sans titre"}</div>
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <span className={"text-[9px] px-1.5 py-0.5 rounded uppercase font-bold " + (m.status === "published" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400")} data-testid={`course-module-status-${m.lesson_id}`}>{m.status === "published" ? "Publié" : "Brouillon"}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 font-bold uppercase inline-flex items-center gap-1"><Package className="w-2.5 h-2.5" /> {n} séquence{n > 1 ? "s" : ""} SCORM</span>
        {m.duration_min > 0 && <span className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.duration_min} min</span>}
      </div>
      {m.description && <p className="text-xs text-slate-400 line-clamp-2 mt-2">{m.description}</p>}
      <ol className="text-xs text-slate-400 space-y-0.5 mt-3 min-h-[2.5rem]">
        {(m.modules || []).slice(0, 4).map((s, i) => <li key={s.module_id} className="truncate">{i + 1}. {s.title}</li>)}
        {(m.modules || []).length > 4 && <li className="text-slate-600">… +{m.modules.length - 4}</li>}
        {n === 0 && <li className="italic text-slate-600">Aucune séquence — ouvrez le module pour importer des SCORM</li>}
      </ol>
      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" onClick={onEdit} className="h-7 bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid={`course-module-edit-${m.lesson_id}`}><Pencil className="w-3 h-3 mr-1" /> Ouvrir</Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 text-red-400 text-xs" data-testid={`course-module-delete-${m.lesson_id}`}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}
